import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { dateTime12 } from 'constants/dateFormat';
import { MANAGE_PATIENT_TRAVEL_REQUEST } from 'constants/userOperations';
import { sortBy } from 'lodash';
import moment from 'moment';
import { userHasAccessTo } from 'services/auth';

import { NSC_ENCOUNTER, NSPP_TRAVEL } from '../../../../../../../services/i18n/namespaces';

import { travelServiceTypeMap, travelStatusMap } from './travelConstants';

export default function useTravelGridData() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const columns = useMemo(
    () => [
      {
        field: 'encounterName',
        headerName: t(`${NSC_ENCOUNTER}:encounter`),
        flex: 1
      },
      {
        field: 'visitDate',
        headerName: t(`${NSPP_TRAVEL}:travelRequests.visitDate`),
        flex: 1,
        valueGetter(value) {
          return value ? new Date(value) : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(dateTime12) : '';
        },
        type: 'date'
      },
      {
        field: 'requestedServices',
        headerName: t(`${NSPP_TRAVEL}:travelRequests.typeOfTravel`),
        flex: 1,
        valueGetter(value) {
          return value
            ? sortBy(value.map(type => t(`${NSPP_TRAVEL}:services.${travelServiceTypeMap[type]}`))).join(' & ')
            : '';
        }
      },
      {
        field: 'status',
        headerName: t(`${NSPP_TRAVEL}:travelRequests.requestStatus`),
        flex: 1,
        minWidth: 180,
        valueGetter(value) {
          return travelStatusMap[value] ? t(`${NSPP_TRAVEL}:statuses.${travelStatusMap[value]}`) : value;
        }
      },
      {
        field: 'actions',
        headerName: t(`${NSPP_TRAVEL}:travelRequests.action`),
        flex: 1,
        maxWidth: 235,
        renderCell({ value, row }) {
          if (row.status || !userHasAccessTo(MANAGE_PATIENT_TRAVEL_REQUEST)) {
            return '';
          }
          return (
            <Button
              size="small"
              variant="contained"
              onClick={function() {
                navigate(
                  `./travel-request?ssuPatientEncounterId=${row.sitePatientEncounterId ||
                    ''}&protocolEncounterKey=${row.protocolEncounterKey || ''}`
                );
              }}
            >
              {t(`${NSPP_TRAVEL}:travelRequests.addTravelRequest`)}
            </Button>
          );
        }
      }
    ],
    [navigate, t]
  );

  return [columns, data, setData];
}
